import React from "react";
import { motion } from "framer-motion";
import { FaAnchor, FaConciergeBell, FaMapMarkedAlt } from "react-icons/fa";

const BenefitColumn = ({ icon: Icon, title, description, index }) => (
  <motion.div
    className="flex flex-col items-center text-center shadow-lg p-8 md:p-12 lg:p-16 border-b md:border-b-0 md:border-r last:border-r-0 border-gray-200 bg-white rounded-b-md md:rounded-none md:first:rounded-l-md md:last:rounded-r-md"
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ // Base transition for initial animation
      duration: 0.5,
      delay: index * 0.2
    }}
    whileHover={{ // Define hover state properties
      scale: 1.02,
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    }}
    // Apply transition to the hover effect separately if needed, or rely on default
    // transition={{ duration: 0.2 }} // Example: Quick transition for hover
  >
    <motion.div
      className="mb-6 text-5xl text-blue-500"
      whileHover={{ // Icon specific hover animation
        scale: 1.1,
        color: "#ef4444",
      }}
      transition={{ duration: 0.2 }} // Transition for the icon hover
    >
      <Icon />
    </motion.div>
    <h3 className="text-xl font-semibold mb-4 JosefinSans text-gray-800">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </motion.div>
);

const Benefits = () => {
  const benefits = [
    {
      icon: FaAnchor,
      title: "All-Inclusive Luxury Cruises",
      description:
        "Relax and enjoy. Our cruises include experienced crew, fuel, taxes, towels, water toys (paddleboards, snorkeling gear), a signature seafood meal, fresh fruit salad, and a selection of beverages.",
    },
    {
      icon: FaConciergeBell,
      title: "Expert Crew & Bespoke Service",
      description:
        "Our professional and friendly crew is dedicated to your safety and comfort, ensuring a seamless experience. We tailor every trip to your preferences for an unforgettable journey.",
    },
    {
      icon: FaMapMarkedAlt,
      title: "Explore Mykonos' Hidden Gems",
      description:
        "Discover secluded beaches, iconic landmarks like Delos & Rhenia, and the stunning South Coastline. Choose from curated itineraries or customize your own unique Mykonos adventure.",
    },
  ];

  return (
    <section id="services" className="py-16 bg-gray-50 overflow-hidden">
      <div className="max-w-[1440px] mx-auto flex flex-col lg:flex-row gap-12 items-center px-4 md:px-8">
        {/* Left Section - Text Content */}
        <motion.div
          className="lg:w-1/3 text-center lg:text-left"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl lg:text-6xl mb-4 JosefinSans text-blue-900 font-semibold"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Our Services
          </motion.h2>
          <motion.p
            className="text-gray-600 text-md mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Experience Unforgettable <b>Mykonos Yacht Charters</b> with Premium Amenities.
          </motion.p>
          <motion.hr
            className="w-24 border-2 border-red-500 mb-4 mx-auto lg:mx-0"
            initial={{ scaleX: 0, originX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          />
        </motion.div>

        {/* Right Section - Benefit Cards */}
        <div className="lg:w-2/3 w-full relative">
          <div className="shadow-xl rounded-md border border-gray-200 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-3">
              {benefits.map((benefit, index) => (
                <BenefitColumn key={index} {...benefit} index={index} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;
